/**
 * Market identity and anchor arithmetic (D4).
 *
 * `market_id = "{asset}-updown-1m-{anchor_epoch_seconds}"`, the anchor aligned
 * to the period. The id shape is deterministic on purpose: at a 60-second
 * cadence the client computes the next market's name and prefetches it sixty
 * times an hour, and a round trip to learn a name it can derive is a round trip
 * on the critical path (docs/04 §2.4).
 *
 * Two things this module refuses to do:
 *
 * - **Assume the period.** `period_seconds` comes from `GET /config` / `GET /series`
 *   and is passed in. The literal `1m` inside the id is part of the *name*, not
 *   a claim about arithmetic, and the two are allowed to be spelled separately.
 * - **Compute a window the server publishes.** `open_at_ms`, `expiry_at_ms` and
 *   `resolve_eta_ms` are absolute values on the market object and the client
 *   renders differences of them (D9). `windowsFor` exists for the one case the
 *   server cannot answer — a market that does not exist yet.
 */

export type Asset = 'btc' | 'eth'
export type Symbol_ = 'BTC-USD' | 'ETH-USD'
export type SeriesId = `${Asset}-updown-1m`

const MARKET_ID_RE = /^(btc|eth)-updown-1m-(\d{1,13})$/
const SERIES_ID_RE = /^(btc|eth)-updown-1m$/

export class MarketIdError extends Error {
  override readonly name = 'MarketIdError'
}

export interface ParsedMarketId {
  seriesId: SeriesId
  asset: Asset
  /** Seconds, as it appears in the id. */
  anchorS: number
  /** Milliseconds, as `anchor_ms` appears on the market object. */
  anchorMs: number
}

export function isMarketId(value: string): boolean {
  return MARKET_ID_RE.test(value)
}

export function isSeriesId(value: string): value is SeriesId {
  return SERIES_ID_RE.test(value)
}

export function parseMarketId(marketId: string): ParsedMarketId {
  const m = MARKET_ID_RE.exec(marketId)
  if (!m) throw new MarketIdError(`not a market_id: ${JSON.stringify(marketId)}`)
  const asset = m[1] as Asset
  const anchorS = Number(m[2])
  return {
    seriesId: `${asset}-updown-1m`,
    asset,
    anchorS,
    // `× 1000` is the whole conversion (§2.2): the id carries seconds because it
    // is an identifier, the object carries ms because it is a timestamp.
    anchorMs: anchorS * 1000,
  }
}

export function buildMarketId(seriesId: SeriesId, anchorS: number): string {
  if (!isSeriesId(seriesId)) throw new MarketIdError(`not a series_id: ${JSON.stringify(seriesId)}`)
  if (!Number.isInteger(anchorS) || anchorS < 0) {
    throw new MarketIdError(`anchor must be a non-negative integer number of seconds; got ${anchorS}`)
  }
  return `${seriesId}-${anchorS}`
}

export function seriesIdFor(asset: Asset): SeriesId {
  return `${asset}-updown-1m`
}

export function symbolFor(asset: Asset): Symbol_ {
  return asset === 'btc' ? 'BTC-USD' : 'ETH-USD'
}

export function assetForSymbol(symbol: Symbol_): Asset {
  return symbol === 'BTC-USD' ? 'btc' : 'eth'
}

/** Every asset this client can render. */
export const ALL_ASSETS: readonly Asset[] = ['btc', 'eth']

/**
 * The assets this venue runs, read off `/config.symbols` (`docs/06` §F138): the
 * venue lists only what it serves, so ETH being off is a fact in the payload and
 * not a constant here. A config with no list at all is treated as every asset.
 */
export function servedAssets(config: unknown): readonly Asset[] {
  const symbols = (config as { symbols?: { symbol?: string; enabled?: boolean }[] } | null)?.symbols
  if (!symbols) return ALL_ASSETS
  const served = ALL_ASSETS.filter((asset) =>
    symbols.some((s) => s.symbol === symbolFor(asset) && s.enabled !== false),
  )
  return served.length > 0 ? served : ALL_ASSETS
}

// ---------------------------------------------------------------------------
// Anchor arithmetic
// ---------------------------------------------------------------------------

/** The anchor of the period containing `serverTimeMs`. `anchor ≡ 0 (mod period)`. */
export function anchorAt(serverTimeMs: number, periodS: number): number {
  assertPeriod(periodS)
  return Math.floor(serverTimeMs / 1000 / periodS) * periodS
}

export function nextAnchor(anchorS: number, periodS: number): number {
  assertPeriod(periodS)
  return anchorS + periodS
}

export function previousAnchor(anchorS: number, periodS: number): number {
  assertPeriod(periodS)
  return anchorS - periodS
}

/**
 * The id of the market `offset` periods from `marketId`. Positive is forward.
 *
 * A computed id is a *prediction*, not a promise: markets exist only
 * `n_ahead` periods in advance, so anything further out answers
 * `MARKET_NOT_FOUND` (§2.4). Callers must handle that rather than assume.
 */
export function shiftMarketId(marketId: string, offset: number, periodS: number): string {
  assertPeriod(periodS)
  const { seriesId, anchorS } = parseMarketId(marketId)
  return buildMarketId(seriesId, anchorS + offset * periodS)
}

export function nextMarketId(marketId: string, periodS: number): string {
  return shiftMarketId(marketId, 1, periodS)
}

export function previousMarketId(marketId: string, periodS: number): string {
  return shiftMarketId(marketId, -1, periodS)
}

/** Chronological order. Ids within one series sort lexically too, but two
 *  series interleave, so compare the anchors. */
export function compareMarketIds(a: string, b: string): -1 | 0 | 1 {
  const left = parseMarketId(a)
  const right = parseMarketId(b)
  if (left.anchorS !== right.anchorS) return left.anchorS < right.anchorS ? -1 : 1
  if (left.seriesId === right.seriesId) return 0
  return left.seriesId < right.seriesId ? -1 : 1
}

// ---------------------------------------------------------------------------
// Windows
// ---------------------------------------------------------------------------

export interface SeriesTiming {
  periodS: number
  strikeWindowS: number
  settlementWindowS: number
}

export interface MarketWindows {
  anchorMs: number
  strikeWindowStartMs: number
  strikeWindowEndMs: number
  expiryAtMs: number
  settlementWindowStartMs: number
  settlementWindowEndMs: number
}

/**
 * Derive one market's timeline from its anchor and the series parameters.
 *
 * For a market the server has already described, read the absolute fields off
 * the market object instead — this is for the market that does not exist yet,
 * which is the one the `<SeriesRail>` and the pre-roll warm-up need.
 *
 * **Both windows run backward** — the strike over `[anchor − W, anchor)` since
 * register §F24, and the settlement over `[expiry − W, expiry)` since §F26. A
 * market's settlement price is therefore decided by its own final `W` seconds
 * and is known the instant it expires, which is what removes the wait between
 * the last trade and the payout.
 *
 * **`settle(N)` and `strike(N+1)` are the same window and the same number
 * again** (§F26). `expiry(N) == anchor(N+1)`, so `[expiry(N) − W, expiry(N))`
 * and `[anchor(N+1) − W, anchor(N+1))` are one interval. That identity held
 * until 2026-08-16, was retired by §F24 when the strike moved, and returns here
 * for a different reason: not to chain the markets but because both prices are
 * now backward-looking and the boundary is shared. One TWAP per anchor serves
 * both markets, which halves the oracle reads.
 *
 * The corollary is that `W` is **one** series parameter, not two.
 * `twap_window_seconds` sets both windows, and it has to — two knobs could be
 * set to different values and the identity would break silently.
 *
 * **`open_at_ms` and `resolve_eta_ms` are deliberately absent**, and the reason
 * survived the change even though one of the two numbers did not. `open_at_ms`
 * is now `anchor_ms` on the happy path, which is tempting to derive here — and
 * wrong to, because a late strike slips it and the server is the sole authority
 * on time (D9). `resolve_eta_ms` still sits `δ` after its window closes, and `δ`
 * is a config value rather than a constant (`market.fixing_latency_budget_ms`,
 * `market.resolve_eta_offset_ms`). A locally derived boundary is one more thing
 * that can disagree with the frame arriving a moment later.
 */
export function windowsFor(anchorS: number, timing: SeriesTiming): MarketWindows {
  assertPeriod(timing.periodS)
  assertWindow(timing.strikeWindowS, 'strikeWindowS')
  assertWindow(timing.settlementWindowS, 'settlementWindowS')
  const anchorMs = anchorS * 1000
  const expiryAtMs = anchorMs + timing.periodS * 1000
  return {
    anchorMs,
    strikeWindowStartMs: anchorMs - timing.strikeWindowS * 1000,
    strikeWindowEndMs: anchorMs,
    expiryAtMs,
    settlementWindowStartMs: expiryAtMs - timing.settlementWindowS * 1000,
    settlementWindowEndMs: expiryAtMs,
  }
}

/**
 * `W` must be at least one second, and this is the one place that is enforced.
 *
 * A window is a **half-open interval**, so `W = 0` gives `[expiry, expiry)`,
 * which contains no prices. The venue's own settlement is a print, not a window
 * (§F74), and does not come through here: this derivation has no caller outside
 * its tests.
 *
 * Caught here rather than downstream because the failure is otherwise silent and
 * total: every market of the series voids, sixty times an hour, and the only
 * symptom is an empty panel. A config that cannot work should fail where it is
 * read, not where it is felt (register §F26).
 */
function assertWindow(windowS: number, name: string): void {
  if (!Number.isInteger(windowS) || windowS <= 0) {
    throw new MarketIdError(
      `${name} must be a positive integer number of seconds; got ${windowS}. ` +
        'A window is an interval — length zero contains no prices, so the market ' +
        'would void rather than settle on a last price. It comes from GET /config.',
    )
  }
}

function assertPeriod(periodS: number): void {
  if (!Number.isInteger(periodS) || periodS <= 0) {
    throw new MarketIdError(
      `period_seconds must be a positive integer; got ${periodS}. It comes from GET /config.`,
    )
  }
}
